import { useMemo } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, format, isSameMonth,
  isSameDay, parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Check } from "lucide-react";

const STATUS_COLOR = {
  requested: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  confirmed: "bg-accent/15 text-accent",
  rescheduled: "bg-primary/10 text-primary",
  completed: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  cancelled: "bg-destructive/10 text-destructive line-through",
};

function apptDate(a) { return parseISO(`${a.date}T${a.time || "00:00"}`); }

export default function CalendarView({ view, cursor, appointments, onSelectDay, typeColors = {} }) {
  const dotColor = (a) => typeColors[a.type] || "#94A3B8";
  const items = appointments || [];

  const month = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    const days = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [cursor]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(cursor, { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [cursor]);

  const dayAppts = (day) => items.filter((a) => isSameDay(apptDate(a), day)).sort((x, y) => (x.time || "").localeCompare(y.time || ""));

  if (view === "month") {
    return (
      <div className="rounded-2xl border border-border overflow-hidden bg-card" data-testid="calendar-month">
        <div className="grid grid-cols-7 text-center text-xs font-medium text-muted-foreground border-b border-border">
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {month.map((day, i) => {
            const list = dayAppts(day);
            const muted = !isSameMonth(day, cursor);
            return (
              <button key={i} onClick={() => onSelectDay?.(day)}
                className={`min-h-[92px] text-left p-1.5 border-b border-r border-border last:border-r-0 hover:bg-secondary/50 transition-colors ${muted ? "bg-muted/30" : ""}`}
                data-testid={`day-${format(day, "yyyy-MM-dd")}`}>
                <div className={`text-xs font-medium ${muted ? "text-muted-foreground" : ""}`}>{format(day, "d")}</div>
                <div className="space-y-0.5 mt-1">
                  {list.slice(0, 3).map((a) => (
                    <div key={a.id} className={`text-[10px] rounded px-1 py-0.5 truncate flex items-center gap-0.5 ${STATUS_COLOR[a.status] || ""}`}>
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dotColor(a) }} />
                      {a.attendance_confirmed && <Check className="w-2.5 h-2.5 text-accent shrink-0" />}
                      {a.time} {a.client_name || a.type}
                    </div>
                  ))}
                  {list.length > 3 && <div className="text-[10px] text-muted-foreground">+{list.length - 3}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (view === "week") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="calendar-week">
        {weekDays.map((day) => {
          const list = dayAppts(day);
          return (
            <div key={day.toISOString()} className="rounded-xl border border-border bg-card p-2 min-h-[160px]">
              <div className="text-xs font-medium text-center pb-2 border-b border-border">
                <div className="text-muted-foreground">{format(day, "EEE", { locale: ptBR })}</div>
                <div className="font-heading text-base">{format(day, "d")}</div>
              </div>
              <div className="space-y-1 mt-2">
                {list.map((a) => (
                  <div key={a.id} className={`text-[11px] rounded-lg px-2 py-1 ${STATUS_COLOR[a.status] || ""}`}>
                    <div className="font-medium flex items-center gap-1"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: dotColor(a) }} />{a.time}</div>
                    <div className="truncate">{a.client_name || a.type}</div>
                    {a.attendance_confirmed && <div className="flex items-center gap-0.5 text-accent mt-0.5"><Check className="w-3 h-3" />presença</div>}
                  </div>
                ))}
                {list.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-2">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // day view
  const list = dayAppts(cursor);
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="calendar-day">
      <div className="font-heading font-medium mb-3">{format(cursor, "EEEE, d 'de' MMMM", { locale: ptBR })}</div>
      {list.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma consulta neste dia.</p> : (
        <div className="space-y-2">
          {list.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-xl border border-border border-l-4 p-3" style={{ borderLeftColor: dotColor(a) }}>
              <div className="font-heading font-semibold text-primary w-14">{a.time}</div>
              <div className="flex-1"><div className="font-medium flex items-center gap-1">{a.client_name || a.type}{a.attendance_confirmed && <Check className="w-3.5 h-3.5 text-accent" />}</div><div className="text-xs text-muted-foreground">{a.type} · R$ {a.value?.toFixed(2)}</div></div>
              <span className={`text-[11px] rounded-full px-2 py-0.5 ${STATUS_COLOR[a.status] || ""}`}>{a.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
